import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sheet } from '@/shared/components/Sheet';
import { Skeleton } from '@/shared/components/Skeleton';
import { PlatformIcon, type PlatformId } from '@/shared/components/PlatformIcon';
import { useToast } from '@/shared/components/Toast';
import { invalidateAccountPlan } from '@/shared/hooks/useBilling';
import { getApiErrorMessage } from '@/shared/lib/api-errors';
import { ROUTES } from '@/shared/constants/routes';
import { planLimitsService, type PlanLimitsOverview } from '@/shared/services/plan-limits.service';

export const PLAN_LIMITS_KEY = ['plan-limits'] as const;

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Ids the owner wants active: what is active now, until they tick otherwise. */
function activeIds(rows: readonly { id: string; paused: boolean }[]): Set<string> {
  return new Set(rows.filter((r) => !r.paused).map((r) => r.id));
}

/**
 * D-01: the owner chooses which brands, channels and team members stay
 * active when the account holds more than its plan allows. Nothing is
 * deleted; what is left unticked is paused (read-only, publishes nothing).
 * The server checks the limits again.
 */
export function ChooseActiveSheet({ open, onClose }: Props): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: PLAN_LIMITS_KEY, queryFn: () => planLimitsService.get(), enabled: open });
  const [brands, setBrands] = useState<Set<string>>(new Set());
  const [channels, setChannels] = useState<Set<string>>(new Set());
  const [members, setMembers] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!data) return;
    setBrands(activeIds(data.brands));
    setChannels(activeIds(data.channels));
    setMembers(activeIds(data.members));
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      planLimitsService.choose({
        ...(data?.limits.brands !== null ? { brandIds: [...brands] } : {}),
        ...(data?.limits.channelsPerBrand !== null ? { channelIds: [...channels] } : {}),
        ...(data?.limits.seatsPerBrand !== null ? { memberIds: [...members].filter((id) => !data?.members.find((m) => m.id === id)?.owner) } : {}),
      }),
    onSuccess: (next: PlanLimitsOverview) => {
      queryClient.setQueryData(PLAN_LIMITS_KEY, next);
      invalidateAccountPlan(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['platforms'] });
      void queryClient.invalidateQueries({ queryKey: ['team'] });
      toast('success', t('planLimits.sheet.saved'));
      onClose();
    },
    onError: (err) => toast('error', getApiErrorMessage(err, t('planLimits.sheet.failed'))),
  });

  const overBrands = data?.limits.brands !== null && data !== undefined && brands.size > (data.limits.brands ?? 0);
  const brandName = useMemo(() => new Map((data?.brands ?? []).map((b) => [b.id, b.name])), [data]);
  const perBrandOver = (ids: Set<string>, rows: readonly { id: string; workspaceId: string; owner?: boolean }[], limit: number | null): boolean =>
    limit !== null && [...brandName.keys()].some((w) => rows.filter((r) => r.workspaceId === w && (ids.has(r.id) || r.owner)).length > limit);
  const tooMany =
    overBrands ||
    (data ? perBrandOver(channels, data.channels, data.limits.channelsPerBrand) || perBrandOver(members, data.members, data.limits.seatsPerBrand) : false);

  const toggle = (set: Set<string>, update: (s: Set<string>) => void, id: string): void => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    update(next);
  };

  const footer = (
    <div className="flex flex-wrap items-center gap-2 w-full">
      <button type="button" className="btn btn-primary" disabled={!data || tooMany || save.isPending} onClick={() => save.mutate()}>
        {t('planLimits.sheet.save')}
      </button>
      <Link to={`${ROUTES.SETTINGS}?tab=subscription`} className="btn btn-ghost no-underline" onClick={onClose}>
        {t('planLimits.sheet.upgrade')}
      </Link>
      {tooMany && <span className="text-meta text-[var(--bad)]" role="status">{t('planLimits.sheet.tooMany')}</span>}
    </div>
  );

  const row = (key: string, checked: boolean, label: ReactElement | string, onChange: () => void, disabled = false, hint?: string): ReactElement => (
    <li key={key}>
      <label className="flex items-center gap-3 min-h-[44px] py-1 cursor-pointer">
        <input type="checkbox" className="h-5 w-5 shrink-0 accent-[var(--color-accent)]" checked={checked} disabled={disabled} onChange={onChange} />
        <span className="min-w-0 flex-1 text-ui text-[var(--ink)] truncate" dir="auto">{label}</span>
        {hint && <span className="badge badge-neutral shrink-0">{hint}</span>}
      </label>
    </li>
  );

  return (
    <Sheet open={open} onClose={onClose} title={t('planLimits.sheet.title')} id="choose-active" footer={footer}>
      <p className="m-0 mb-4 text-ui text-[var(--ink-2)] leading-relaxed">{t('planLimits.sheet.intro')}</p>
      {isLoading && <div className="space-y-2" aria-busy="true"><Skeleton height={44} /><Skeleton height={44} /><Skeleton height={44} /></div>}
      {isError && (
        <div role="alert" className="flex items-center justify-between gap-3 rounded-[10px] p-2.5 text-ui bg-[var(--bad-soft)] text-[var(--bad)]">
          <span>{t('planLimits.sheet.loadFailed')}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void refetch()}>{t('common.retry')}</button>
        </div>
      )}
      {data && (
        <div className="space-y-5">
          {data.limits.brands !== null && data.brands.length > data.limits.brands && (
            <section>
              <h3 className="stat-label m-0 mb-1">{t('planLimits.sheet.brands', { active: brands.size, limit: data.limits.brands })}</h3>
              <ul className="list-none m-0 p-0">
                {data.brands.map((b) => row(b.id, brands.has(b.id), b.name, () => toggle(brands, setBrands, b.id), false, b.paused ? t('planLimits.sheet.paused') : undefined))}
              </ul>
            </section>
          )}
          {data.limits.channelsPerBrand !== null &&
            data.brands.map((b) => {
              const own = data.channels.filter((c) => c.workspaceId === b.id);
              if (own.length <= (data.limits.channelsPerBrand ?? 0)) return null;
              return (
                <section key={`c-${b.id}`}>
                  <h3 className="stat-label m-0 mb-1">
                    {t('planLimits.sheet.channels', { brand: b.name, active: own.filter((c) => channels.has(c.id)).length, limit: data.limits.channelsPerBrand })}
                  </h3>
                  <ul className="list-none m-0 p-0">
                    {own.map((c) =>
                      row(
                        c.id,
                        channels.has(c.id),
                        <span className="inline-flex items-center gap-2"><PlatformIcon platform={c.platform.toLowerCase() as PlatformId} size={20} decorative />{c.username ?? c.platform}</span>,
                        () => toggle(channels, setChannels, c.id),
                        false,
                        c.paused ? t('planLimits.sheet.paused') : undefined,
                      ),
                    )}
                  </ul>
                </section>
              );
            })}
          {data.limits.seatsPerBrand !== null &&
            data.brands.map((b) => {
              const team = data.members.filter((m) => m.workspaceId === b.id);
              if (team.length <= (data.limits.seatsPerBrand ?? 0)) return null;
              return (
                <section key={`m-${b.id}`}>
                  <h3 className="stat-label m-0 mb-1">
                    {t('planLimits.sheet.members', { brand: b.name, active: team.filter((m) => m.owner || members.has(m.id)).length, limit: data.limits.seatsPerBrand })}
                  </h3>
                  <ul className="list-none m-0 p-0">
                    {team.map((m) =>
                      row(
                        m.id,
                        m.owner || members.has(m.id),
                        m.name || m.email || m.role,
                        () => toggle(members, setMembers, m.id),
                        m.owner,
                        m.owner ? t('planLimits.sheet.owner') : m.paused ? t('planLimits.sheet.paused') : undefined,
                      ),
                    )}
                  </ul>
                </section>
              );
            })}
        </div>
      )}
    </Sheet>
  );
}
